import type { LC, PropsWithChildren, LiveElement } from '@use-gpu/live';
import type { RenderViewType } from '@use-gpu/core';
import type { LightEnv, PassResources, PassFlags, RenderComponents } from '../pass/types';

import { use, yeet, memo, useMemo, useOne } from '@use-gpu/live';

import { PassReconciler } from '../reconcilers/index';
import { useRenderContext } from '../providers/render-provider';

import { DebugPass } from '../pass/debug-pass';
import { MotionPass } from '../pass/motion-pass';
import { NormalPass } from '../pass/normal-pass';
import { OITPass } from '../pass/oit-pass';
import { OutlinePass } from '../pass/outline-pass';
import { PickingPass } from '../pass/picking-pass';
import { ShadowPass } from '../pass/shadow-pass';
import { SSAOPass } from '../pass/ssao-pass';

import { DebugRender } from './forward/debug';
import { NormalRender } from './forward/normal';
import { PickingRender } from './forward/picking';
import { ShadedRender } from './forward/shaded';
import { ShadowRender } from './forward/shadow';
import { SolidRender } from './forward/solid';
import { UIRender } from './forward/ui';

import { OITShadedRender } from './oit/oit-shaded';
import { OITSolidRender } from './oit/oit-solid';

import { useStandardBindGroups } from '../pass/bindings';
import { useMakeUseVariants } from '../pass/variants';

import { ColorPass } from '../pass/color-pass';
import { ColorCubePass } from '../pass/color-cube-pass';

import { LightProvider } from '../providers/light-provider';
import { LightMaterialProvider } from '../providers/light-material-provider';
import { Renderer } from './renderer';

const {quote} = PassReconciler;

const DEFAULT_PASS: Record<RenderViewType, LC<any>> = {
  '2d': ColorPass,
  'cube': ColorCubePass,
};

const NO_RESOURCES: PassResources = {
  targets: {},
  bindings: {},
  dispatches: [],
  views: {},
};

const NO_OPTIONS: Record<string, any> = {};

export type ForwardRendererFlags = Pick<PassFlags, 'lights' | 'shadows' | 'color' | 'merge' | 'overlay'>;

export type ForwardRendererProps = PropsWithChildren<{
  resources: PassResources,
  options?: ForwardRendererFlags,
  passes?: LiveElement[],
  subpasses?: LiveElement[],
  components?: RenderComponents,
}>;

const getComponents = ({modes = {}, renders = {}}: Partial<RenderComponents>): RenderComponents => {
  return {
    modes: {
      debug: DebugRender,
      picking: PickingRender,
      shadow: ShadowRender,
      normal: NormalRender,
      ...modes,
    },
    renders: {
      solid: {opaque: SolidRender, transparent: SolidRender, oit: OITSolidRender},
      shaded: {opaque: ShadedRender, transparent: ShadedRender, oit: OITShadedRender},
      ui: {opaque: UIRender, transparent: UIRender},
      ...renders,
    }
  }
};

/** Forward-mode rendering with lights immediately evaluated in-shader */
export const ForwardRenderer: LC<ForwardRendererProps> = memo((props: ForwardRendererProps) => {
  const {
    resources = NO_RESOURCES,
    options = NO_OPTIONS,
    passes,
    subpasses,

    children,
  } = props;

  const {targets} = resources;

  const {
    color = true,
    overlay = false,
    merge = false,
    overscan = 0,
    debug = null,

    facets = false,
    lights = false,
    motion = !!targets.motion,
    normals = !!targets.normal,
    oit = !!targets.oit,
    outline = targets.outline ? {} : undefined,
    picking = !!targets.picking,
    shadows = !!targets.shadow,
    ssao = targets.ssao ? {} : undefined,
  } = options as Record<string, any>;

  const extendedFlags = useMemo(() => ({
    color,
    overlay,
    merge,
    overscan,

    facets,
    lights,
    motion,
    normals,
    oit,
    outline,
    picking,
    shadows,
    ssao,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [options, targets]);

  const components = useOne(() => getComponents(props.components ?? {}), props.components);

  // Adapt to view type (2d or cube)
  const {viewType} = useRenderContext();

  // Prepare passes
  const resolved = useMemo(() => passes ?? [
    normals ? use(NormalPass, options) : null,
    motion ? use(MotionPass, options) : null,
    ssao ? use(SSAOPass, options) : null,
    shadows ? use(ShadowPass, options) : null,
    color ? use(DEFAULT_PASS[viewType], options) : null,
    oit ? use(OITPass, options) : null,
    outline ? use(OutlinePass, options) : null,
    picking ? use(PickingPass, options) : null,
    debug ? use(DebugPass, options) : null,
    subpasses,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [props, viewType]);

  // Add resource dispatches to render
  const dispatches = resources.dispatches?.length ? yeet({ dispatch: resources.dispatches }) : null;
  const combined = dispatches ? [
    quote(dispatches),
    children,
  ] : children;

  // Gather lights via capture provider
  const view = lights ? use(LightProvider, {
    shadows,
    children: combined,
    then: (light: LightEnv) =>
      useOne(() => quote(yeet({ env: { light }})), light)
  }) : combined;

  // Pass bindings
  const bindGroups = useStandardBindGroups(resources, extendedFlags);

  // Render variants
  const variants = useMakeUseVariants(components, extendedFlags);

  return (
    Renderer({
      resources,
      bindGroups,
      options,

      variants,
      passes: resolved,

      children: view,
      decorate: lights
        ? (children: LiveElement) => use(LightMaterialProvider, {shadows, children})
        : undefined,
    })
  );
}, 'ForwardRenderer');
